/* eslint-disable react/prop-types */
import React from "react";
import { Check, ExternalLink, Heart, Loader2 } from "lucide-react";

// The "Answer the Call" walkthrough — ONE component, shared by both surfaces that
// ask an employee to answer a Buster Call: the notification banner row
// (NotificationBanner) and the Past Calls card inside BusterCallDialog.
//
// Why it exists (Levi, thread 1a121f716c9243a3): both surfaces used to show a
// bare pair of buttons — "Open" and "Show Support" — with nothing saying what the
// employee was meant to do between them. The order is the whole task (leave the
// review, screenshot it, THEN upload), and without it spelled out the second
// button reads as an alternative to the first rather than the step after it.
// Levi's words: "it needs to walk people through what they need to do ... It
// needs to be dummy proof."
//
// The sentence is rating-aware on purpose: a call carries the star rating its
// submitter asked for, and "leave a 5 star review" is simply wrong advice on a
// call that asks for one star. `rating`/`company` are interpolated when the
// caller knows them and the sentence falls back to rating-agnostic wording when
// it does not — the bell row written by the original broadcast carries neither,
// while the banner row injected by notificationController.received() carries
// both.

const StepNumber = ({ n, done }) => (
  <span
    className={`shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-extrabold ${
      done
        ? "bg-emerald-500/25 text-emerald-300 ring-1 ring-emerald-500/50"
        : "bg-zinc-700/70 text-zinc-200"
    }`}
  >
    {done ? <Check className="h-3 w-3" /> : n}
  </span>
);

const OPEN_BTN =
  "shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-zinc-600 bg-zinc-800 px-2.5 py-1 text-[11px] font-semibold text-zinc-200 hover:bg-zinc-700 active:scale-95 transition-all";
const SUPPORT_BTN =
  "shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/50 bg-emerald-500/15 px-2.5 py-1 text-[11px] font-bold text-emerald-300 hover:bg-emerald-500/25 active:scale-95 transition-all shadow-sm shadow-emerald-500/10 disabled:opacity-60 disabled:pointer-events-none";

const AnswerSteps = ({
  company = null,
  rating = null,
  opened = false,
  submitting = false,
  onOpen,
  onShowSupport,
}) => {
  // "<n> star" stays SINGULAR in every case: here it is a compound adjective in
  // front of "review" ("leave a 5 star review"), not a count of stars. The first
  // version pluralised it and the rendered card read "leave a 5 stars review" —
  // caught on the live page, not in the diff.
  const stars = rating ? `${rating} star` : null;
  const forCompany = company ? ` for ${company}` : "";

  return (
    <div className="mt-2 rounded-lg border border-orange-500/25 bg-zinc-950/50 p-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-orange-300">
          How to answer the call
        </span>
        <span className="text-[10px] font-semibold text-amber-300">100 pts</span>
      </div>

      {/* Step 1 — leave the review */}
      <div className="flex items-start gap-2">
        <StepNumber n={1} done={opened} />
        <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
          <p className="text-[11px] leading-snug text-zinc-300">
            Tap <span className="font-semibold text-white">Open</span> and leave{" "}
            {stars ? (
              <span className="font-semibold text-white">a {stars} review</span>
            ) : (
              "a review"
            )}
            {forCompany} on Google.
          </p>
          {typeof onOpen === "function" && (
            <button type="button" onClick={onOpen} className={OPEN_BTN}>
              <ExternalLink className="h-3 w-3" />
              Open
            </button>
          )}
        </div>
      </div>

      {/* Step 2 — screenshot it. No button: this is the step that happens in
          another app, and there is nothing on this screen to tap for it. */}
      <div className="flex items-start gap-2">
        <StepNumber n={2} />
        <p className="flex-1 min-w-0 text-[11px] leading-snug text-zinc-300">
          Screenshot the review you left.
        </p>
      </div>

      {/* Step 3 — upload the screenshot */}
      <div className="flex items-start gap-2">
        <StepNumber n={3} />
        <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
          <p className="text-[11px] leading-snug text-zinc-300">
            Tap <span className="font-semibold text-white">Show Support</span> and upload that
            screenshot.
          </p>
          {typeof onShowSupport === "function" && (
            <button
              type="button"
              onClick={onShowSupport}
              disabled={submitting}
              className={SUPPORT_BTN}
            >
              {submitting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Heart className="h-3 w-3 fill-current" />
              )}
              {submitting ? "Submitting…" : "Show Support"}
            </button>
          )}
        </div>
      </div>

      <p className="text-[10px] leading-snug text-zinc-500 pt-0.5 border-t border-zinc-800">
        Points land after the President approves your screenshot.
      </p>
    </div>
  );
};

export default AnswerSteps;
